import React from 'react';
import { Card, CardContent, Typography, Button, Badge } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ParticipantView } from '@app/views';
import { useNavigate } from 'react-router-dom';
import { palette } from '@app/palette';
import { useGroupViewCreator } from '@app/view-creator';
import { useUnreadCounts } from '@app/hooks/useUnreadCounts';
import { InfoBubble } from './InfoBubble';
import MessagingSection from './MessagingSection';

// Styled card matching existing theme
const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: 16,
}));

export interface TeamCardProps {
  participants: ParticipantView[];
  cohortId: number;
  cohortName?: string;
  accountabilityPartners?: number[];
}

export const TeamCard: React.FC<TeamCardProps> = ({
  participants,
  cohortId,
  cohortName,
  accountabilityPartners = [],
}) => {
  const { data: groupData, isLoading: viewLoading } = useGroupViewCreator(Number(cohortId));
  // once groupData is loaded, grab its channel; before that, use an empty string
  const teamChannelId = groupData?.channel ?? '';
  const { teamChatCount, dmCounts } = useUnreadCounts(teamChannelId, cohortId);

  const navigate = useNavigate();

  const handleTeamChatClick = () => {
    navigate(`/team-chat/${cohortId}/`);
  };

  const handleDirectMessageClick = (participantId: number) => {
    navigate(`/direct-message/${cohortId}/${participantId}`);
  };

  //
  // 1) Sort participants:
  //    • Participants with no .name (loading placeholders) go to the bottom.
  //    • Then Accountability Partners first.
  //    • Then “Haters”  (role = 'Participant'), alphabetically by first name
  //    • Then Coach (role = 'Coach')
  //    • Then Captain (role = 'Captain')
  //
  const sorted = [...participants].sort((a, b) => {
    // a.name / b.name presence check:
    const aHasName = Boolean(a.name);
    const bHasName = Boolean(b.name);
    if (!aHasName && bHasName) return 1; // a is loading, push to bottom
    if (aHasName && !bHasName) return -1; // b is loading, a comes first

    // Both either have names or both lack names: proceed
    // If both lack names, keep original order
    if (!aHasName && !bHasName) {
      return 0;
    }

    // Now both have names → sort by AP status first
    const aIsAP = accountabilityPartners.includes(a.CohortUser.id);
    const bIsAP = accountabilityPartners.includes(b.CohortUser.id);
    if (aIsAP && !bIsAP) return -1;
    if (!aIsAP && bIsAP) return 1;

    // Role rank for remaining:
    const rankOf = (p: ParticipantView) => {
      const r = p.CohortUser.role.toLowerCase();
      if (r === 'captain') return 3;
      if (r === 'coach') return 2;
      return 1; // “Participant” or anything else → rank 1
    };
    const aRank = rankOf(a);
    const bRank = rankOf(b);
    if (aRank !== bRank) return aRank - bRank;

    // If both are “Haters” (rank 1), sort by first name:
    if (aRank === 1 && bRank === 1) {
      const aFirst = a.name.split(' ')[0].toLowerCase();
      const bFirst = b.name.split(' ')[0].toLowerCase();
      return aFirst.localeCompare(bFirst);
    }

    // Otherwise same rank (both coaches or both captains) → sort by full name
    return a.name.localeCompare(b.name);
  });

  const dmParticipants = sorted.filter((p) => {
    const role = p.CohortUser.role?.toLowerCase?.() || '';
    return role !== 'coach' && role !== 'captain';
  });

  const supportParticipants = sorted.filter((p) => {
    const role = p.CohortUser.role?.toLowerCase?.() || '';
    return role === 'coach' || role === 'captain';
  });

  return (
    <StyledCard>
      <CardContent className="team-card-content">
        <div id="team-card-header" style={{ position: 'relative' }}>
          <div
            className="title-with-info"
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 6, // space between title and info icon
              flexWrap: 'nowrap',
            }}
          >
            <Typography variant="h5" component={'div'} color={palette.primary.main}>
              Chat
            </Typography>
            <InfoBubble title="Chat Your Haters">
              In Chat you can send a chat message to the whole team, or click on the name of a team member (aka “hater”)
              to message them directly. The badges indicate your accountability partner, exercise coach, and team
              captain. Team members show up in the chat after they log in the first time.
            </InfoBubble>
            <Badge
              badgeContent={teamChatCount}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              sx={{
                marginLeft: 'auto', // pushes it to the right
                '& .MuiBadge-badge': {
                  backgroundColor: '#4DBB21',
                  color: 'white',
                  boxShadow: '0px 4px 4px 0px #0000001F',
                  width: '26px',
                  height: '26px',
                  borderRadius: '100px',
                  fontWeight: 500,
                },
              }}
            >
              <Button
                variant={'contained'}
                onClick={() => handleTeamChatClick()}
                sx={{ padding: '10px', fontSize: '18px' }}
              >
                <img
                  src="/assets/svg/chat-room.svg"
                  alt="Team Chat"
                  style={{ marginRight: '5px', width: '17px', height: '17px' }}
                />
                Team chat
              </Button>
            </Badge>
          </div>
        </div>

        <div id="team-card-participant-list" style={{ marginTop: 14 }}>
          <MessagingSection
            iconSource="/assets/svg/paper-plane-icon.svg"
            title="Direct Messaging"
            participants={dmParticipants}
            dmCounts={dmCounts}
            accountabilityPartners={accountabilityPartners}
            onRowClick={(id) => handleDirectMessageClick(Number(id))}
            defaultOpen={false}
          />

          <hr
            style={{
              borderColor: 'rgba(109, 109, 109, 0.43)',
              borderWidth: '1px 0 0 0',
              borderStyle: 'solid',
              marginTop: '20px',
            }}
          />

          <MessagingSection
            iconSource="/assets/svg/support-icon.svg"
            title="Support Chat"
            participants={supportParticipants}
            dmCounts={dmCounts}
            onRowClick={(id) => handleDirectMessageClick(Number(id))}
            defaultOpen={false}
          />
        </div>

        {participants.some((participant) => !Boolean(participant.name)) && (
          <section style={{ display: 'flex', flex: '1 1 100%', marginTop: '16px' }}>
            <Typography variant={'body1'} color={'primary'}>
              {participants.filter((participant) => !Boolean(participant.name)).length} more haters are on your team and
              will show up for messaging after their first login. Keep checking back!
            </Typography>
          </section>
        )}
      </CardContent>
    </StyledCard>
  );
};
